/**
 * useRemotePositionTracker — Synthesizes store.position for remote renderer playback.
 *
 * WebAudioRenderer.handleTimeUpdate drives store.updateTime() by reading
 * audio.currentTime. For network renderers (UPnP, RAAT), the <audio> element
 * is paused, so handleTimeUpdate never fires and position stays at 0.
 *
 * When the active zone is a network renderer and playbackState is 'playing',
 * a 1-second interval increments position from a known anchor point — the same
 * approach iOS MPNowPlayingInfoCenter uses (set elapsed + rate, interpolate).
 *
 * Tauri desktop: timer-based renderers (PMG/Ideon/MU2/AirLens) and exclusive
 * bit-perfect USB DAC playback have no browser audio element / position events —
 * wall-clock ticker runs for those only. Position-reporting UPnP renderers use
 * Connector renderer-position-changed events.
 */
import { useEffect, useRef } from 'react';
import { useMaestroStore } from '../store/maestroStore';
import { startUpnpStatePoll, stopUpnpStatePoll, getActivePollerZoneId } from '../transport/upnpStatePoller';
import {
  shouldUseWallClockPositionTicker,
  runWallClockPositionTickCycle,
} from '../transport/remotePositionTicker';
import { TransportCommand } from '../transport/TransportCommand';
import { detectPlatform } from '../utils/platform';

function isNetworkRenderer(zone: any): boolean {
  if (!zone?.type) return false;
  const localTypes = ['browser', 'local', 'local-native'];
  const localIds = ['this_computer', 'local_computer', 'this_device'];
  return !localTypes.includes(zone.type) && !localIds.includes(zone.id);
}

export function useRemotePositionTracker(): void {
  const anchorTimeRef = useRef(0);
  const anchorPositionRef = useRef(0);

  useEffect(() => {
    const plat = detectPlatform();

    const interval = setInterval(() => {
      const s = useMaestroStore.getState();
      const zone = s.discovery?.activeZone;
      const exclusiveActive = TransportCommand.isExclusiveActive();

      if (!shouldUseWallClockPositionTicker(
        plat,
        zone,
        s.activeConnectorRenderer,
        exclusiveActive,
      )) {
        if (anchorTimeRef.current !== 0) anchorTimeRef.current = 0;
        return;
      }

      const cycle = runWallClockPositionTickCycle({
        platform: plat,
        zone,
        renderer: s.activeConnectorRenderer,
        exclusiveActive,
        playbackState: s.playbackState,
        currentTrack: s.currentTrack,
        position: s.position || 0,
        duration: s.duration || 0,
        anchorTimeMs: anchorTimeRef.current,
        anchorPosition: anchorPositionRef.current,
        nowMs: Date.now(),
      });

      anchorTimeRef.current = cycle.anchorTimeMs;
      anchorPositionRef.current = cycle.anchorPosition;

      if (!cycle.storeWrite) {
        if (s.playbackState !== 'playing' || !s.currentTrack) {
          anchorTimeRef.current = 0;
          anchorPositionRef.current = 0;
        }
        return;
      }

      s.updateTime(cycle.storeWrite.position, cycle.storeWrite.duration);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // UPnP ground-truth state poller — browser HTTPS only.
  // Tauri desktop: Connector emits renderer-position-changed (position-reporting renderers).
  // iOS: native bridge owns position.
  useEffect(() => {
    const plat = detectPlatform();
    if (plat.isIOSNativeApp || plat.isTauriDesktop) return;

    const unsubPoller = useMaestroStore.subscribe((state) => {
      const zone = state.discovery?.activeZone as any;
      const isRemote = isNetworkRenderer(zone);
      const zoneId = zone?.id || '';
      const isRaat = typeof zoneId === 'string' && zoneId.startsWith('raat-uuid:');
      const transportType = (zone?.metadata?.transport_type || zone?.transport_type || '') as string;
      const isUpnp = !isRaat && (transportType === 'upnp' || zone?.type === 'upnp'
        || (isRemote && !transportType));
      const isPlaying = state.playbackState === 'playing';

      if (isRemote && isUpnp && isPlaying) {
        if (getActivePollerZoneId() !== zone?.id) {
          startUpnpStatePoll(zone);
        }
      } else {
        if (getActivePollerZoneId()) {
          stopUpnpStatePoll();
        }
      }
    });

    return () => {
      unsubPoller();
      stopUpnpStatePoll();
    };
  }, []);

  // Detect seek (position jump > 3s), track changes, and pause→resume to reset anchor
  useEffect(() => {
    let lastTrackId: string | null = null;
    let lastPosition = 0;
    let lastPlaybackState: string | null = null;

    const unsubscribe = useMaestroStore.subscribe((state) => {
      const trackId = state.currentTrack?.id || null;

      if (trackId !== lastTrackId) {
        lastTrackId = trackId;
        anchorTimeRef.current = 0;
        anchorPositionRef.current = 0;
        lastPosition = 0;
        lastPlaybackState = state.playbackState;
        return;
      }

      if (state.playbackState === 'playing' && lastPlaybackState !== 'playing') {
        anchorTimeRef.current = 0;
        anchorPositionRef.current = 0;
      }
      lastPlaybackState = state.playbackState;

      const delta = Math.abs(state.position - lastPosition);
      if (delta > 3 && anchorTimeRef.current !== 0) {
        anchorTimeRef.current = Date.now();
        anchorPositionRef.current = state.position;
      }

      lastPosition = state.position;
    });

    return unsubscribe;
  }, []);
}
